import { api } from './index'

export type PermissionLevel = 'view' | 'manage'
export type RoleType = 'preset' | 'custom'

export type PermissionDetail = {
  resource_name: string
  resource_display_name: string
  group_name: string
  can_view: boolean
  can_manage: boolean
}

export type Role = {
  id: number
  name: string
  display_name: string
  description: string | null
  role_type: RoleType
  merchant_id: number | null
  active: boolean
  preset: boolean
  permissions: Record<string, string[]>
  permission_details: PermissionDetail[]
  users_count?: number
  location_ids: number[]
  created_at: string
  updated_at: string
}

export type PermissionResource = {
  id: number
  name: string
  display_name: string
  group_name: string
  group_display_name: string
  description: string
  sort_order: number
}

export type GroupedPermissions = {
  group_name: string
  group_display_name: string
  resources: PermissionResource[]
}

type GetRolesResponse = { roles: Role[] }
type GetRoleResponse = { role: Role }
type GetGroupedPermissionsResponse = {
  groups: GroupedPermissions[]
}

export type PermissionPayload = {
  resource_name: string
  can_view: boolean
  can_manage: boolean
}

type CreateRolePayload = {
  name: string
  display_name: string
  description?: string
  permissions: PermissionPayload[]
}

type UpdateRolePayload = {
  id: number
  name?: string
  display_name?: string
  description?: string
  permissions?: PermissionPayload[]
}

export const rolesApi = api.injectEndpoints({
  endpoints: builder => ({
    getRoles: builder.query<GetRolesResponse, void>({
      query: () => ({ url: 'roles' }),
      providesTags: ['Role'],
    }),

    getRole: builder.query<GetRoleResponse, number>({
      query: id => ({ url: `roles/${id}` }),
      providesTags: ['Role'],
    }),

    createRole: builder.mutation<Role, CreateRolePayload>({
      query: payload => ({
        url: 'roles',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Role'],
    }),

    updateRole: builder.mutation<Role, UpdateRolePayload>({
      query: ({ id, ...body }) => ({
        url: `roles/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Role'],
    }),

    deleteRole: builder.mutation<void, number>({
      query: id => ({
        url: `roles/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Role'],
    }),

    getGroupedPermissions: builder.query<GetGroupedPermissionsResponse, void>({
      query: () => ({ url: 'permission_resources/grouped' }),
      providesTags: ['PermissionResource'],
    }),
  }),
})

export const {
  useGetRolesQuery,
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetGroupedPermissionsQuery,
} = rolesApi
